import { MEDICATION_SYSTEM_CATEGORIES_KEY } from "./medication-system-categories.js";
import type { WorkspaceChangedScopeV1 } from "./workspace-refresh-contract.js";

export const tenantAdminOutboundCallsSummaryContractVersion =
  "tenant-admin-outbound-calls-summary-v1" as const;

export const tenantAdminOutboundCallsSummaryRoutePath =
  "/api/v1/tenant-admin/outbound-calls/summary" as const;

export const tenantAdminOutboundCallsCampaignsContractVersion =
  "tenant-admin-outbound-calls-campaigns-v1" as const;

export const tenantAdminOutboundCallsCampaignsRoutePath =
  "/api/v1/tenant-admin/outbound-calls/campaigns" as const;

export const tenantAdminOutboundCallsQuestionnairesContractVersion =
  "tenant-admin-outbound-calls-questionnaires-v1" as const;

export const tenantAdminOutboundCallsQuestionnairesRoutePath =
  "/api/v1/tenant-admin/outbound-calls/questionnaires" as const;

export const tenantAdminOutboundCallsTargetsContractVersion =
  "tenant-admin-outbound-calls-targets-v1" as const;

export const tenantAdminOutboundCallsTargetsRoutePath =
  "/api/v1/tenant-admin/outbound-calls/targets" as const;

export const tenantAdminOutboundCallsSessionsContractVersion =
  "tenant-admin-outbound-calls-sessions-v1" as const;

export const tenantAdminOutboundCallsSessionsRoutePath =
  "/api/v1/tenant-admin/outbound-calls/sessions" as const;

export const tenantAdminOutboundCallsCommandContractVersion =
  "tenant-admin-outbound-calls-command-v1" as const;

export const tenantAdminOutboundCallsCommandRoutePath =
  "/api/v1/tenant-admin/outbound-calls/commands" as const;

export type TenantAdminOutboundQuestionnaireTemplateStatusV1 =
  | "draft"
  | "active"
  | "retired";

export type TenantAdminOutboundQuestionnaireVersionStatusV1 =
  | "draft"
  | "pending_review"
  | "approved"
  | "active"
  | "retired";

export type TenantAdminOutboundQuestionAnswerTypeV1 =
  | "yes_no"
  | "single_choice"
  | "multi_choice"
  | "short_text"
  | "number"
  | "date"
  | "confirmation";

export type TenantAdminOutboundCampaignStatusV1 =
  | "draft"
  | "active"
  | "paused"
  | "completed"
  | "archived";

export type TenantAdminOutboundTargetStatusV1 =
  | "eligible"
  | "scheduled"
  | "calling"
  | "paused"
  | "completed"
  | "suppressed"
  | "cancelled"
  | "expired"
  | "review_required";

export type TenantAdminOutboundConsentStateV1 =
  | "unknown"
  | "confirmed"
  | "declined"
  | "not_required";

export type TenantAdminOutboundTargetAuthorizationStateV1 =
  | "confirmed"
  | "not_required";

export type TenantAdminOutboundSessionStatusV1 =
  | "not_started"
  | "scheduled"
  | "calling"
  | "identity_verification"
  | "verified"
  | "in_progress"
  | "paused"
  | "completed"
  | "failed"
  | "cancelled"
  | "review_required";

export type TenantAdminOutboundIdentityVerificationStatusV1 =
  | "not_started"
  | "pending"
  | "verified"
  | "failed"
  | "wrong_person";

export type TenantAdminOutboundAttemptStatusV1 =
  | "queued"
  | "dialing"
  | "live_answer"
  | "voicemail"
  | "no_answer"
  | "busy"
  | "failed"
  | "disconnected"
  | "completed"
  | "cancelled";

export type TenantAdminOutboundDialerModeV1 =
  | "direct_connect"
  | "connect_campaign";

export type TenantAdminOutboundCampaignDialerStatusV1 =
  | "unconfigured"
  | "ready"
  | "blocked"
  | "disabled";

export type TenantAdminOutboundVoicemailPolicyV1 =
  | "none"
  | "minimal_message"
  | "callback_only";

export type TenantAdminOutboundRecordingPolicyV1 =
  | "disabled"
  | "after_notice"
  | "explicit_consent";

export type TenantAdminOutboundTimezoneModeV1 =
  | "patient_local"
  | "tenant_default"
  | "fixed";

export type TenantAdminOutboundVoiceTonePresetV1 =
  | "warm_clinical"
  | "gentle_concise"
  | "formal_concise"
  | "custom";

export type TenantAdminOutboundVoiceIntroModeV1 =
  | "provider_first_powered_by_amplecare"
  | "provider_only_white_label"
  | "amplecare_first";

export interface TenantAdminOutboundQuestionnaireVoicePolicyV1 {
  tonePreset: TenantAdminOutboundVoiceTonePresetV1;
  customToneInstructions?: string;
  introMode: TenantAdminOutboundVoiceIntroModeV1;
  fullIntroText: string;
  briefIntroText: string;
  answerInstructionText: string;
  privacyDisclosureText: string;
  clinicianReviewDisclosureText: string;
  disclosureVersion: string;
}

export type TenantAdminOutboundQuestionnaireReviewSignalV1 =
  | "none"
  | "yellow_flag"
  | "red_flag";

export type TenantAdminOutboundQuestionnaireReviewPriorityV1 =
  | "routine"
  | "soon"
  | "urgent";

export interface TenantAdminOutboundQuestionnaireReviewLabelsV1 {
  notSure?: string;
  yellowFlag?: string;
  redFlag?: string;
  changedAnswer?: string;
  medicationReview?: string;
}

export interface TenantAdminOutboundQuestionnaireReviewPriorityMappingV1 {
  yellowFlag?: TenantAdminOutboundQuestionnaireReviewPriorityV1;
  redFlag?: TenantAdminOutboundQuestionnaireReviewPriorityV1;
  notSure?: TenantAdminOutboundQuestionnaireReviewPriorityV1;
  changedAnswer?: TenantAdminOutboundQuestionnaireReviewPriorityV1;
  medicationReview?: TenantAdminOutboundQuestionnaireReviewPriorityV1;
}

export interface TenantAdminOutboundQuestionnaireReviewPolicyV1
  extends Record<string, unknown> {
  policyKey?: string;
  unsureRequiresReviewDefault?: boolean;
  unsureRouting?: TenantAdminOutboundQuestionnaireReviewSignalV1;
  yellowFlagRouting?: "none" | "clinician_review_required" | "queue_review";
  redFlagRouting?: "none" | "clinician_review_required" | "stop_and_review";
  redFlagStopAutomationDefault?: boolean;
  reviewQueueKey?: string;
  reviewPriorityMapping?: TenantAdminOutboundQuestionnaireReviewPriorityMappingV1;
  reviewLabels?: TenantAdminOutboundQuestionnaireReviewLabelsV1;
  clinicianReviewDisclosureRequired?: boolean;
  voicePolicy?: TenantAdminOutboundQuestionnaireVoicePolicyV1;
}

export type TenantAdminOutboundEventKindV1 =
  | "target_created"
  | "target_suppressed"
  | "policy_evaluated"
  | "attempt_queued"
  | "attempt_started"
  | "attempt_outcome"
  | "identity_verified"
  | "identity_failed"
  | "question_answered"
  | "session_paused"
  | "resume_scheduled"
  | "resume_attempted"
  | "session_completed"
  | "session_failed"
  | "review_required"
  | "opt_out"
  | "red_flag"
  | "manual_update";

export type TenantAdminOutboundEventActorKindV1 =
  | "system"
  | "admin"
  | "clinician"
  | "patient"
  | "connect"
  | "scheduler";

export const tenantAdminOutboundCallCommandKindsV1 = [
  "create-questionnaire-template",
  "update-questionnaire-template",
  "duplicate-questionnaire-template",
  "archive-questionnaire-template",
  "publish-questionnaire-version",
  "reactivate-questionnaire-version",
  "create-campaign",
  "update-campaign",
  "duplicate-campaign",
  "update-campaign-questionnaire-version",
  "update-campaign-dialer-config",
  "validate-campaign-readiness",
  "pause-campaign",
  "resume-campaign",
  "archive-campaign",
  "flag-patient-for-outbound-call",
  "bulk-create-outbound-call-targets",
  "cancel-outbound-call-target",
  "mark-outbound-call-suppression",
  "manually-schedule-outbound-call-attempt",
  "run-outbound-scheduler-dry-run",
  "start-outbound-direct-call",
  "submit-outbound-campaign-dial-batch",
  "assign-outbound-session-review",
] as const;

export type TenantAdminOutboundCallCommandKindV1 =
  (typeof tenantAdminOutboundCallCommandKindsV1)[number];

export function isTenantAdminOutboundCallCommandKindV1(
  value: unknown,
): value is TenantAdminOutboundCallCommandKindV1 {
  return (
    typeof value === "string" &&
    tenantAdminOutboundCallCommandKindsV1.includes(
      value as TenantAdminOutboundCallCommandKindV1,
    )
  );
}

export interface TenantAdminOutboundCallPolicyWindowV1 {
  timezoneMode: TenantAdminOutboundTimezoneModeV1;
  defaultTimezone: string | null;
  allowedDays: string[];
  allowedStartLocalTime: string;
  allowedEndLocalTime: string;
  quietHours: Record<string, unknown>;
  blackoutDates: string[];
}

export interface TenantAdminOutboundCampaignPolicyV1 {
  policyId: string;
  campaignId: string;
  policyKey: string;
  window: TenantAdminOutboundCallPolicyWindowV1;
  maxAttemptsPerDay: number;
  maxAttemptsTotal: number;
  minRetryCooldownMinutes: number;
  callCapMinutes: number;
  voicemailPolicy: TenantAdminOutboundVoicemailPolicyV1;
  recordingPolicy: TenantAdminOutboundRecordingPolicyV1;
  resumePolicy: Record<string, unknown>;
  optOutPolicy: Record<string, unknown>;
  policyMetadata: Record<string, unknown>;
  updatedAt: string;
}

export interface TenantAdminOutboundQuestionnaireQuestionMetadataV1 {
  plainLanguageDefinition?: string;
  approvedDefinition?: string;
  definitionStatus?: string;
  medicationReconciliation?: Record<string, unknown>;
  sourceNote?: string;
  sourceReviewNote?: string;
  sourceSection?: string;
  [key: string]: unknown;
}

export interface TenantAdminOutboundQuestionnaireQuestionV1 {
  questionId: string;
  questionKey: string;
  sectionKey: string;
  sectionLabel: string | null;
  sequenceNumber: number;
  promptText: string;
  answerType: TenantAdminOutboundQuestionAnswerTypeV1;
  required: boolean;
  voicePhraseConfig: Record<string, unknown>;
  dtmfConfig: Record<string, unknown>;
  validationRules: Record<string, unknown>;
  skipLogic: TenantAdminOutboundQuestionSkipLogicV1;
  redFlagRules: Record<string, unknown>;
  answerNormalization: TenantAdminOutboundAnswerNormalizationV1;
  clinicianReviewLabel: string | null;
  questionMetadata: TenantAdminOutboundQuestionnaireQuestionMetadataV1;
}

export interface TenantAdminOutboundAnswerNormalizationOptionV1 {
  value: string;
  label: string;
  spokenAliases: string[];
}

export type TenantAdminOutboundConfirmationClassV1 =
  | "silent"
  | "confirm_back"
  | "always_confirm";

/**
 * Voice rendering + normalization config for a choice question. All keys are
 * optional and an index signature is kept so the historical `{}` payloads and
 * marker keys (e.g. `medicationReconciliation`) remain valid; use
 * `parseAnswerNormalizationV1` to read with defaults applied.
 */
export interface TenantAdminOutboundAnswerNormalizationV1 {
  options?: TenantAdminOutboundAnswerNormalizationOptionV1[];
  enumerateAloud?: boolean;
  confirmationClass?: TenantAdminOutboundConfirmationClassV1;
  unlistedPolicy?: "capture_verbatim";
  [key: string]: unknown;
}

export interface ParsedAnswerNormalizationV1 {
  options: TenantAdminOutboundAnswerNormalizationOptionV1[];
  enumerateAloud: boolean;
  confirmationClass: TenantAdminOutboundConfirmationClassV1;
  unlistedPolicy: "capture_verbatim";
}

const confirmationClassesV1: readonly TenantAdminOutboundConfirmationClassV1[] =
  ["silent", "confirm_back", "always_confirm"];

export function parseAnswerNormalizationV1(
  value: unknown,
): ParsedAnswerNormalizationV1 {
  const raw =
    value && typeof value === "object" && !Array.isArray(value)
      ? (value as TenantAdminOutboundAnswerNormalizationV1)
      : {};
  const options = Array.isArray(raw.options)
    ? raw.options.filter(
        (option): option is TenantAdminOutboundAnswerNormalizationOptionV1 =>
          Boolean(
            option &&
              typeof option === "object" &&
              typeof (option as { value?: unknown }).value === "string" &&
              typeof (option as { label?: unknown }).label === "string",
          ),
      )
      .map((option) => ({
        value: option.value,
        label: option.label,
        spokenAliases: Array.isArray(option.spokenAliases)
          ? option.spokenAliases.filter(
              (alias): alias is string => typeof alias === "string",
            )
          : [],
      }))
    : [];

  return {
    options,
    enumerateAloud: raw.enumerateAloud === true,
    confirmationClass: confirmationClassesV1.includes(
      raw.confirmationClass as TenantAdminOutboundConfirmationClassV1,
    )
      ? (raw.confirmationClass as TenantAdminOutboundConfirmationClassV1)
      : "confirm_back",
    unlistedPolicy: "capture_verbatim",
  };
}

export interface TenantAdminOutboundQuestionnaireVersionV1 {
  questionnaireVersionId: string;
  questionnaireTemplateId: string;
  versionNumber: number;
  versionStatus: TenantAdminOutboundQuestionnaireVersionStatusV1;
  versionLabel: string | null;
  estimatedMinutes: number | null;
  resumePolicy: Record<string, unknown>;
  reviewPolicy: TenantAdminOutboundQuestionnaireReviewPolicyV1;
  authoredByDisplay: string | null;
  clinicalApprovedByDisplay: string | null;
  clinicalApprovedAt: string | null;
  publishedAt: string | null;
  retiredAt: string | null;
  questionCount: number;
  questions?: TenantAdminOutboundQuestionnaireQuestionV1[];
  updatedAt: string;
}

export interface TenantAdminOutboundQuestionnaireTemplateV1 {
  questionnaireTemplateId: string;
  questionnaireTemplateKey: string;
  displayName: string;
  clinicalPurpose: string;
  status: TenantAdminOutboundQuestionnaireTemplateStatusV1;
  locale: string;
  ownerDepartmentCode: string | null;
  defaultEstimatedMinutes: number | null;
  defaultResumePolicy: Record<string, unknown>;
  defaultReviewPolicy: TenantAdminOutboundQuestionnaireReviewPolicyV1;
  activeVersion: TenantAdminOutboundQuestionnaireVersionV1 | null;
  latestVersion: TenantAdminOutboundQuestionnaireVersionV1 | null;
  createdAt: string;
  updatedAt: string;
}

export interface TenantAdminOutboundCampaignV1 {
  campaignId: string;
  campaignKey: string;
  displayName: string;
  campaignStatus: TenantAdminOutboundCampaignStatusV1;
  campaignKind: string;
  purposeText: string;
  questionnaireTemplateId: string | null;
  questionnaireTemplateVersionId: string | null;
  questionnaireDisplayName: string | null;
  questionnaireVersionNumber: number | null;
  eligibilityRules: Record<string, unknown>;
  reviewRoutingRules: Record<string, unknown>;
  dialerConfig?: TenantAdminOutboundCampaignDialerConfigV1;
  policy: TenantAdminOutboundCampaignPolicyV1 | null;
  startsAt: string | null;
  endsAt: string | null;
  targetCounts: Record<TenantAdminOutboundTargetStatusV1, number>;
  createdByDisplay: string | null;
  createdAt: string;
  updatedAt: string;
}

export type TenantAdminOutboundCampaignQuestionnairePropagationModeV1 =
  | "impact_review"
  | "apply_approved";

export interface TenantAdminOutboundCampaignQuestionnaireImpactReviewV1 {
  campaignId: string;
  campaignDisplayName: string;
  currentQuestionnaireTemplateId: string | null;
  currentQuestionnaireTemplateVersionId: string | null;
  currentQuestionnaireVersionNumber: number | null;
  targetQuestionnaireTemplateId: string | null;
  targetQuestionnaireTemplateVersionId: string;
  targetQuestionnaireVersionNumber: number | null;
  targetQuestionCount: number;
  queuedTargetCount: number;
  eligibleSessionCount: number;
  immutableSessionCount: number;
  inProgressSessionCount: number;
  pausedSessionCount: number;
  completedSessionCount: number;
  failedSessionCount: number;
  sessionsAlreadyOnTargetCount: number;
  sessionsThatWillRemainPinnedCount: number;
  migratedSessionCount: number;
  migrationApplied: boolean;
  migrateEligibleQueuedSessions: boolean;
  approvalToken: string;
  policyDiff: {
    resumePolicyChanged: boolean;
    reviewPolicyChanged: boolean;
  };
}

export interface TenantAdminOutboundCampaignDialerReadinessV1 {
  directCallReady: boolean;
  campaignBatchReady: boolean;
  blockerMessages: string[];
}

export interface TenantAdminOutboundCampaignDialerConfigV1 {
  dialerMode: TenantAdminOutboundDialerModeV1;
  dialerStatus: TenantAdminOutboundCampaignDialerStatusV1;
  connectInstanceId: string | null;
  connectCampaignId: string | null;
  connectCampaignArn: string | null;
  connectCampaignRegion: string | null;
  connectContactFlowId: string | null;
  connectSourcePhoneNumber: string | null;
  readiness: TenantAdminOutboundCampaignDialerReadinessV1;
  metadata: Record<string, unknown>;
}

export interface TenantAdminOutboundTargetV1 {
  targetId: string;
  campaignId: string;
  campaignDisplayName: string;
  patientId: string;
  patientDisplayName: string;
  patientIdentifier: string | null;
  targetKey: string;
  targetStatus: TenantAdminOutboundTargetStatusV1;
  targetReasonKey: string;
  phoneNumberE164: string;
  patientTimezone: string | null;
  preferredCallWindow: Record<string, unknown>;
  consentState: TenantAdminOutboundConsentStateV1;
  consentCapturedAt: string | null;
  consentSource: string | null;
  suppressionReasonKey: string | null;
  dueAt: string | null;
  expiresAt: string | null;
  nextEligibleAttemptAt: string | null;
  queuedDirectCallAttemptId: string | null;
  queuedDirectCallScheduledFor: string | null;
  queuedDirectCallSessionId: string | null;
  attemptCount: number;
  lastAttemptAt: string | null;
  lastAttemptOutcome: string | null;
  flaggedByDisplay: string | null;
  flaggedAt: string;
  updatedAt: string;
}

export interface TenantAdminOutboundAnswerV1 {
  answerId: string;
  questionKey: string;
  answerStatus: "answered" | "skipped" | "changed" | "redacted";
  normalizedAnswer: Record<string, unknown>;
  rawAnswerText: string | null;
  confidenceLevel: "low" | "medium" | "high" | null;
  redFlagTriggered: boolean;
  clinicianReviewRequired: boolean;
  answeredAt: string;
}

export interface TenantAdminOutboundAttemptV1 {
  attemptId: string;
  targetId: string;
  sessionId: string | null;
  communicationId: string | null;
  attemptKey: string;
  attemptNumber: number;
  attemptStatus: TenantAdminOutboundAttemptStatusV1;
  dialerMode: TenantAdminOutboundDialerModeV1;
  connectContactId: string | null;
  connectCampaignId: string | null;
  connectDialRequestId: string | null;
  callerIdNumber: string | null;
  destinationPhoneNumberE164: string;
  scheduledFor: string | null;
  dialStartedAt: string | null;
  connectedAt: string | null;
  disconnectedAt: string | null;
  outcomeReasonKey: string | null;
  errorCode: string | null;
  errorMessage: string | null;
}

export interface TenantAdminOutboundEventV1 {
  eventId: string;
  eventKey: string;
  eventKind: TenantAdminOutboundEventKindV1;
  actorKind: TenantAdminOutboundEventActorKindV1;
  actorReference: string | null;
  eventSummary: string;
  policyDecision: Record<string, unknown>;
  eventPayload: Record<string, unknown>;
  occurredAt: string;
}

export interface TenantAdminOutboundQuestionnaireVersionPinningV1 {
  reasonKey: string;
  existingQuestionnaireTemplateVersionId: string | null;
  attemptedQuestionnaireTemplateVersionId: string | null;
  preservedTotalQuestionCount: number | null;
  attemptedTotalQuestionCount: number | null;
  recordedAt: string | null;
}

export interface TenantAdminOutboundSessionV1 {
  sessionId: string;
  targetId: string;
  questionnaireTemplateVersionId: string;
  patientId: string;
  patientDisplayName: string;
  campaignDisplayName: string;
  sessionKey: string;
  sessionStatus: TenantAdminOutboundSessionStatusV1;
  identityVerificationStatus: TenantAdminOutboundIdentityVerificationStatusV1;
  currentQuestionKey: string | null;
  answeredQuestionCount: number;
  totalQuestionCount: number;
  redFlagCount: number;
  startedAt: string | null;
  verifiedAt: string | null;
  pausedAt: string | null;
  completedAt: string | null;
  cancelledAt: string | null;
  failedAt: string | null;
  resumeReasonKey: string | null;
  dueAt: string | null;
  expiresAt: string | null;
  sessionSummary: string | null;
  answers?: TenantAdminOutboundAnswerV1[];
  attempts?: TenantAdminOutboundAttemptV1[];
  events?: TenantAdminOutboundEventV1[];
  questionnaireVersionPinning?: TenantAdminOutboundQuestionnaireVersionPinningV1 | null;
  updatedAt: string;
}

export interface TenantAdminOutboundCallsSummaryRequestV1 {
  tenantId: string;
  campaignId?: string | null;
  analyticsStartAt?: string | null;
  analyticsEndAt?: string | null;
  generatedAt?: string | null;
}

export interface TenantAdminOutboundCallsAnalyticsPointV1 {
  dateKey: string;
  attemptedCallCount: number;
  liveAnswerCount: number;
  voicemailAttemptCount: number;
  noAnswerAttemptCount: number;
  busyAttemptCount: number;
  failedAttemptCount: number;
  completedSessionCount: number;
  reviewRequiredSessionCount: number;
  optOutEventCount: number;
  redFlagEventCount: number;
}

export interface TenantAdminOutboundCallsAnalyticsSnapshotV1 {
  windowStartAt: string;
  windowEndAt: string;
  attemptedCallCount: number;
  liveAnswerCount: number;
  voicemailAttemptCount: number;
  noAnswerAttemptCount: number;
  busyAttemptCount: number;
  failedAttemptCount: number;
  completedSessionCount: number;
  reviewRequiredSessionCount: number;
  optOutEventCount: number;
  redFlagEventCount: number;
  answerRatePercent: number;
  completionRatePercent: number;
  reviewRequiredRatePercent: number;
  averageAnsweredQuestionCount: number;
  estimatedMinutesSaved: number;
  series: TenantAdminOutboundCallsAnalyticsPointV1[];
}

export interface TenantAdminOutboundCallsAuditEventKindCountV1 {
  eventKind: TenantAdminOutboundEventKindV1;
  count: number;
  latestOccurredAt: string | null;
}

export interface TenantAdminOutboundCallsAuditActorKindCountV1 {
  actorKind: TenantAdminOutboundEventActorKindV1;
  count: number;
  latestOccurredAt: string | null;
}

export interface TenantAdminOutboundCallsAuditSnapshotV1 {
  windowStartAt: string;
  windowEndAt: string;
  totalEventCount: number;
  latestEventAt: string | null;
  eventKindCounts: TenantAdminOutboundCallsAuditEventKindCountV1[];
  actorKindCounts: TenantAdminOutboundCallsAuditActorKindCountV1[];
  recentEvents: TenantAdminOutboundEventV1[];
}

export interface TenantAdminOutboundCallsSummaryResponseV1 {
  schemaVersion: typeof tenantAdminOutboundCallsSummaryContractVersion;
  tenantId: string;
  generatedAt: string;
  gate: {
    liveDialingEnabled: boolean;
    outboundConnectEnabled: boolean;
    outboundQuestionnaireFlowConfigured: boolean;
    campaignApiReachable: boolean;
    blockerMessages: string[];
  };
  metrics: {
    activeCampaignCount: number;
    scheduledTargetCount: number;
    dueTodayCount: number;
    inProgressSessionCount: number;
    pausedSessionCount: number;
    completedSessionCount: number;
    reviewRequiredSessionCount: number;
    failedAttemptCount: number;
    repeatedNoInputPauseSessionCount: number;
    medicationCaptureReviewNeededCount: number;
    scheduledStaleReconciliationCount: number;
  };
  analytics: TenantAdminOutboundCallsAnalyticsSnapshotV1;
  audit: TenantAdminOutboundCallsAuditSnapshotV1;
  campaigns: TenantAdminOutboundCampaignV1[];
  recentTargets: TenantAdminOutboundTargetV1[];
  recentSessions: TenantAdminOutboundSessionV1[];
}

export interface TenantAdminOutboundCallsCampaignsRequestV1 {
  tenantId: string;
  status?: TenantAdminOutboundCampaignStatusV1 | "all" | null;
  limit?: number | null;
  offset?: number | null;
}

export interface TenantAdminOutboundCallsCampaignsResponseV1 {
  schemaVersion: typeof tenantAdminOutboundCallsCampaignsContractVersion;
  tenantId: string;
  generatedAt: string;
  items: TenantAdminOutboundCampaignV1[];
  page: TenantAdminOutboundPageV1;
}

export interface TenantAdminOutboundCallsQuestionnairesRequestV1 {
  tenantId: string;
  status?: TenantAdminOutboundQuestionnaireTemplateStatusV1 | "all" | null;
  includeQuestions?: boolean | null;
  limit?: number | null;
  offset?: number | null;
}

export interface TenantAdminOutboundCallsQuestionnairesResponseV1 {
  schemaVersion: typeof tenantAdminOutboundCallsQuestionnairesContractVersion;
  tenantId: string;
  generatedAt: string;
  items: TenantAdminOutboundQuestionnaireTemplateV1[];
  page: TenantAdminOutboundPageV1;
}

export interface TenantAdminOutboundCallsTargetsRequestV1 {
  tenantId: string;
  campaignId?: string | null;
  status?: TenantAdminOutboundTargetStatusV1 | "all" | null;
  patientId?: string | null;
  limit?: number | null;
  offset?: number | null;
}

export interface TenantAdminOutboundCallsTargetsResponseV1 {
  schemaVersion: typeof tenantAdminOutboundCallsTargetsContractVersion;
  tenantId: string;
  generatedAt: string;
  items: TenantAdminOutboundTargetV1[];
  page: TenantAdminOutboundPageV1;
}

export interface TenantAdminOutboundCallsSessionsRequestV1 {
  tenantId: string;
  campaignId?: string | null;
  targetId?: string | null;
  patientId?: string | null;
  status?: TenantAdminOutboundSessionStatusV1 | "all" | null;
  includeTimeline?: boolean | null;
  limit?: number | null;
  offset?: number | null;
}

export interface TenantAdminOutboundCallsSessionsResponseV1 {
  schemaVersion: typeof tenantAdminOutboundCallsSessionsContractVersion;
  tenantId: string;
  generatedAt: string;
  items: TenantAdminOutboundSessionV1[];
  page: TenantAdminOutboundPageV1;
}

export interface TenantAdminOutboundPageV1 {
  offset: number;
  limit: number;
  totalCount: number;
  hasMore: boolean;
}

export type TenantAdminOutboundCallCommandInputV1 =
  | {
      kind: "create-questionnaire-template" | "update-questionnaire-template";
      questionnaireTemplateId?: string | null;
      questionnaireTemplateKey: string;
      displayName: string;
      clinicalPurpose: string;
      status?: TenantAdminOutboundQuestionnaireTemplateStatusV1 | null;
      locale?: string | null;
      ownerDepartmentCode?: string | null;
      defaultEstimatedMinutes?: number | null;
      defaultResumePolicy?: Record<string, unknown> | null;
      defaultReviewPolicy?: TenantAdminOutboundQuestionnaireReviewPolicyV1 | null;
      templateMetadata?: Record<string, unknown> | null;
    }
  | {
      kind: "duplicate-questionnaire-template";
      sourceQuestionnaireTemplateId: string;
      questionnaireTemplateId?: string | null;
      questionnaireTemplateKey?: string | null;
      displayName?: string | null;
      status?: TenantAdminOutboundQuestionnaireTemplateStatusV1 | null;
      reasonText?: string | null;
    }
  | {
      kind: "archive-questionnaire-template";
      questionnaireTemplateId: string;
    }
  | {
      kind: "publish-questionnaire-version";
      questionnaireTemplateId: string;
      questionnaireVersionId?: string | null;
      versionNumber?: number | null;
      versionLabel?: string | null;
      estimatedMinutes?: number | null;
      resumePolicy?: Record<string, unknown> | null;
      reviewPolicy?: TenantAdminOutboundQuestionnaireReviewPolicyV1 | null;
      questions: TenantAdminOutboundQuestionnaireQuestionInputV1[];
      /** Chart bindings for the new version (chart_bindings on the version
       *  row — how each answer lands in the clinical chart). Omitted: the
       *  bindings of the version being retired are carried forward by
       *  question key. Either way every question must end up bound when the
       *  template carries bindings at all: VIP v9 was published through this
       *  command with 231 questions and 0 bindings and printed an empty chart. */
      chartBindings?: TenantAdminOutboundQuestionnaireChartBindingInputV1[] | null;
      clinicalApproval: TenantAdminOutboundClinicalApprovalInputV1;
    }
  | {
      /** Rollback. Publishing retires the previous version and campaigns only
       *  accept an ACTIVE version, so a bad release could not be undone by
       *  repointing — the retired version had to be republished as yet another
       *  version. This makes a previously published (retired) version active
       *  again, retires the current one and repoints its campaigns; sessions
       *  already in progress keep the version they are pinned to. */
      kind: "reactivate-questionnaire-version";
      questionnaireTemplateVersionId: string;
      /** Default true: campaigns on the version being retired move to the
       *  reactivated one. */
      repointCampaigns?: boolean | null;
      reasonText?: string | null;
    }
  | {
      kind: "create-campaign" | "update-campaign";
      campaignId?: string | null;
      campaignKey: string;
      displayName: string;
      campaignStatus?: TenantAdminOutboundCampaignStatusV1 | null;
      questionnaireTemplateVersionId?: string | null;
      campaignKind?: string | null;
      purposeText: string;
      eligibilityRules?: Record<string, unknown> | null;
      reviewRoutingRules?: Record<string, unknown> | null;
      startsAt?: string | null;
      endsAt?: string | null;
      policy?: TenantAdminOutboundCampaignPolicyInputV1 | null;
      dialer?: TenantAdminOutboundCampaignDialerConfigInputV1 | null;
    }
  | {
      kind: "duplicate-campaign";
      sourceCampaignId: string;
      campaignId?: string | null;
      campaignKey?: string | null;
      displayName?: string | null;
      campaignStatus?: TenantAdminOutboundCampaignStatusV1 | null;
      questionnaireTemplateVersionId?: string | null;
      reasonText?: string | null;
    }
  | {
      kind: "update-campaign-questionnaire-version";
      campaignId: string;
      questionnaireTemplateVersionId: string;
      propagationMode?: TenantAdminOutboundCampaignQuestionnairePropagationModeV1 | null;
      impactReviewApprovalToken?: string | null;
      migrateEligibleQueuedSessions?: boolean | null;
      reasonText?: string | null;
    }
  | {
      kind: "update-campaign-dialer-config";
      campaignId: string;
      dialer: TenantAdminOutboundCampaignDialerConfigInputV1;
      reasonText?: string | null;
    }
  | {
      kind: "validate-campaign-readiness";
      campaignId: string;
      reasonText?: string | null;
    }
  | {
      kind: "pause-campaign" | "resume-campaign" | "archive-campaign";
      campaignId: string;
      reasonText?: string | null;
    }
  | {
      kind: "flag-patient-for-outbound-call";
      campaignId: string;
      patientId: string;
      targetReasonKey: string;
      phoneNumberE164: string;
      patientTimezone?: string | null;
      preferredCallWindow?: Record<string, unknown> | null;
      consentState: TenantAdminOutboundTargetAuthorizationStateV1;
      consentSource: string;
      dueAt?: string | null;
      expiresAt?: string | null;
      notes?: string | null;
    }
  | {
      kind: "bulk-create-outbound-call-targets";
      campaignId: string;
      patientIds: string[];
      targetReasonKey: string;
      consentState: TenantAdminOutboundTargetAuthorizationStateV1;
      consentSource: string;
      defaultDueAt?: string | null;
      defaultExpiresAt?: string | null;
      notes?: string | null;
    }
  | {
      kind: "cancel-outbound-call-target";
      targetId: string;
      reasonText?: string | null;
    }
  | {
      kind: "mark-outbound-call-suppression";
      patientId?: string | null;
      targetId?: string | null;
      suppressionReasonKey: string;
      notes?: string | null;
    }
  | {
      kind: "manually-schedule-outbound-call-attempt";
      targetId: string;
      scheduledFor: string;
      reasonText?: string | null;
    }
  | {
      kind: "run-outbound-scheduler-dry-run";
      campaignId?: string | null;
      limit?: number | null;
      schedulerNow?: string | null;
      reasonText?: string | null;
    }
  | {
      kind: "start-outbound-direct-call";
      attemptId?: string | null;
      targetId?: string | null;
      sessionId?: string | null;
      reasonText?: string | null;
    }
  | {
      kind: "submit-outbound-campaign-dial-batch";
      campaignId?: string | null;
      limit?: number | null;
      schedulerNow?: string | null;
      reasonText?: string | null;
    }
  | {
      kind: "assign-outbound-session-review";
      sessionId: string;
      ownerKind: "clinician" | "team" | "department" | "queue";
      ownerReference: string;
      notes?: string | null;
    };

/** One chart binding as accepted by publish-questionnaire-version. Structural
 *  mirror of ClinicianWorkbenchQuestionnaireFieldBinding (that contract
 *  imports this one, so the shape is repeated rather than imported); the
 *  control plane validates it with validateQuestionnaireFieldBindingV1. */
export interface TenantAdminOutboundQuestionnaireChartBindingInputV1 {
  questionKey: string;
  fieldPath: string;
  transform?: string | null;
  chartPackDestination?: Record<string, unknown> | null;
}

export interface TenantAdminOutboundQuestionnaireQuestionInputV1 {
  questionKey: string;
  sectionKey?: string | null;
  sectionLabel?: string | null;
  sequenceNumber: number;
  promptText: string;
  answerType: TenantAdminOutboundQuestionAnswerTypeV1;
  required?: boolean | null;
  voicePhraseConfig?: Record<string, unknown> | null;
  dtmfConfig?: Record<string, unknown> | null;
  validationRules?: Record<string, unknown> | null;
  skipLogic?: TenantAdminOutboundQuestionSkipLogicV1 | null;
  redFlagRules?: Record<string, unknown> | null;
  answerNormalization?: TenantAdminOutboundAnswerNormalizationV1 | null;
  clinicianReviewLabel?: string | null;
  questionMetadata?: TenantAdminOutboundQuestionnaireQuestionMetadataV1 | null;
}

export type TenantAdminOutboundQuestionBranchComparatorV1 =
  | boolean
  | string
  | number;

export interface TenantAdminOutboundQuestionBranchConditionV1 {
  /** OR-composition: holds when ANY sub-condition holds. AND-ed with every
   *  other constraint on this condition, and freely nestable. */
  anyOf?: TenantAdminOutboundQuestionBranchConditionV1[];
  questionKey?: string;
  equals?: TenantAdminOutboundQuestionBranchComparatorV1;
  oneOf?: TenantAdminOutboundQuestionBranchComparatorV1[];
  containsAny?: string[];
  valuesAnyOf?: string[];
  answerStatus?: "answered" | "skipped" | "changed" | "redacted";
  patientAgeYearsAtLeast?: number;
  patientAgeYearsAtMost?: number;
  patientSexAtBirthOneOf?: string[];
  patientSexAtBirthNotOneOf?: string[];
}

export interface TenantAdminOutboundQuestionBranchRuleV1 {
  when?: TenantAdminOutboundQuestionBranchConditionV1;
  condition?: TenantAdminOutboundQuestionBranchConditionV1;
  if?: TenantAdminOutboundQuestionBranchConditionV1;
  nextQuestionKey?: string | null;
  goToQuestionKey?: string | null;
  end?: boolean;
}

export interface TenantAdminOutboundQuestionSkipLogicV1 {
  /**
   * Conditional display for a question. The VIP One MNET pattern uses this
   * for follow-up prompts such as "ask details only if the prior answer is yes."
   */
  onlyIf?: TenantAdminOutboundQuestionBranchConditionV1;
  /**
   * Explicit jumps evaluated after this question is answered. Use this for
   * yes/no and choice-based branching when a response should skip over later
   * questions or end the call path.
   */
  branches?: TenantAdminOutboundQuestionBranchRuleV1[];
  defaultNextQuestionKey?: string | null;
  endIfNoBranchMatches?: boolean;
}

export interface TenantAdminOutboundQuestionnaireBranchValidationResultV1 {
  ok: boolean;
  errors: string[];
}

function isRecordValue(value: unknown): value is Record<string, unknown> {
  return value != null && typeof value === "object" && !Array.isArray(value);
}

function readBranchConditionRecord(
  value: unknown,
): TenantAdminOutboundQuestionBranchConditionV1 | null {
  return isRecordValue(value)
    ? (value as TenantAdminOutboundQuestionBranchConditionV1)
    : null;
}

function conditionHasPatientPredicate(
  condition: TenantAdminOutboundQuestionBranchConditionV1,
): boolean {
  return (
    typeof condition.patientAgeYearsAtLeast === "number" ||
    typeof condition.patientAgeYearsAtMost === "number" ||
    (Array.isArray(condition.patientSexAtBirthOneOf) &&
      condition.patientSexAtBirthOneOf.length > 0) ||
    (Array.isArray(condition.patientSexAtBirthNotOneOf) &&
      condition.patientSexAtBirthNotOneOf.length > 0)
  );
}

function readBranchTarget(rule: TenantAdminOutboundQuestionBranchRuleV1): string | null {
  const nextQuestionKey =
    typeof rule.nextQuestionKey === "string"
      ? rule.nextQuestionKey.trim()
      : typeof rule.goToQuestionKey === "string"
        ? rule.goToQuestionKey.trim()
        : "";

  return nextQuestionKey || null;
}

function conditionHasSupportedPredicate(
  condition: TenantAdminOutboundQuestionBranchConditionV1,
): boolean {
  return (
    "equals" in condition ||
    (Array.isArray(condition.oneOf) && condition.oneOf.length > 0) ||
    (Array.isArray(condition.containsAny) && condition.containsAny.length > 0) ||
    (Array.isArray(condition.valuesAnyOf) && condition.valuesAnyOf.length > 0) ||
    typeof condition.answerStatus === "string" ||
    conditionHasPatientPredicate(condition)
  );
}

function readBranchRuleCondition(
  rule: TenantAdminOutboundQuestionBranchRuleV1,
): TenantAdminOutboundQuestionBranchConditionV1 | null {
  return (
    readBranchConditionRecord(rule.when) ??
    readBranchConditionRecord(rule.condition) ??
    readBranchConditionRecord(rule.if)
  );
}

export function validateTenantAdminOutboundQuestionnaireBranchingV1(
  questions: TenantAdminOutboundQuestionnaireQuestionInputV1[],
): TenantAdminOutboundQuestionnaireBranchValidationResultV1 {
  const errors: string[] = [];
  const questionKeys = new Set<string>();
  const branchSourceAnswerTypes = new Set<TenantAdminOutboundQuestionAnswerTypeV1>([
    "yes_no",
    "confirmation",
    "single_choice",
    "multi_choice",
  ]);

  for (const question of questions) {
    const questionKey = question.questionKey.trim();

    if (!questionKey) {
      errors.push("Outbound questionnaire branch validation found a blank question key.");
      continue;
    }

    if (questionKeys.has(questionKey)) {
      errors.push(`Outbound questionnaire question key "${questionKey}" is duplicated.`);
    }

    questionKeys.add(questionKey);
  }

  const validateCondition = (input: {
    condition: TenantAdminOutboundQuestionBranchConditionV1;
    ownerQuestionKey: string;
    path: string;
    requireQuestionKey: boolean;
  }) => {
    const referencedQuestionKey =
      typeof input.condition.questionKey === "string"
        ? input.condition.questionKey.trim()
        : "";

    const hasPatientPredicate = conditionHasPatientPredicate(input.condition);

    // anyOf composition (umbrella-flow gates): the OR-branch carries the
    // source/predicate requirements; each sub-condition is validated with
    // the same rules.
    const anyOfConditions = Array.isArray(input.condition.anyOf)
      ? input.condition.anyOf
      : [];

    anyOfConditions.forEach((subCondition, index) => {
      const parsed = readBranchConditionRecord(subCondition);

      if (!parsed) {
        errors.push(`${input.path} anyOf[${index}] is not a condition object.`);
        return;
      }

      validateCondition({
        condition: parsed,
        ownerQuestionKey: input.ownerQuestionKey,
        path: `${input.path} anyOf[${index}]`,
        requireQuestionKey: true,
      });
    });

    if (
      input.requireQuestionKey &&
      !referencedQuestionKey &&
      !hasPatientPredicate &&
      anyOfConditions.length === 0
    ) {
      errors.push(`${input.path} must include a source questionKey.`);
    }

    if (
      referencedQuestionKey &&
      !questionKeys.has(referencedQuestionKey) &&
      referencedQuestionKey !== MEDICATION_SYSTEM_CATEGORIES_KEY
    ) {
      errors.push(
        `${input.path} references missing question "${referencedQuestionKey}".`,
      );
    }

    if (referencedQuestionKey === input.ownerQuestionKey) {
      errors.push(`${input.path} cannot depend on its own question.`);
    }

    if (
      !conditionHasSupportedPredicate(input.condition) &&
      anyOfConditions.length === 0
    ) {
      errors.push(
        `${input.path} must include equals, oneOf, valuesAnyOf, containsAny, answerStatus, or patient demographic predicates.`,
      );
    }
  };

  for (const question of questions) {
    const questionKey = question.questionKey.trim();
    const skipLogic = isRecordValue(question.skipLogic) ? question.skipLogic : {};
    const onlyIf = readBranchConditionRecord(skipLogic.onlyIf);
    const branches = Array.isArray(skipLogic.branches) ? skipLogic.branches : [];
    const defaultNextQuestionKey =
      typeof skipLogic.defaultNextQuestionKey === "string"
        ? skipLogic.defaultNextQuestionKey.trim()
        : "";

    if (onlyIf) {
      validateCondition({
        condition: onlyIf,
        ownerQuestionKey: questionKey,
        path: `skipLogic.onlyIf for "${questionKey}"`,
        requireQuestionKey: true,
      });
    }

    if (defaultNextQuestionKey && !questionKeys.has(defaultNextQuestionKey)) {
      errors.push(
        `skipLogic.defaultNextQuestionKey for "${questionKey}" references missing question "${defaultNextQuestionKey}".`,
      );
    }

    if (branches.length > 0 && !branchSourceAnswerTypes.has(question.answerType)) {
      errors.push(
        `skipLogic.branches for "${questionKey}" requires a yes/no, confirmation, single choice, or multi choice answer type.`,
      );
    }

    for (const [index, rawRule] of branches.entries()) {
      if (!isRecordValue(rawRule)) {
        errors.push(`skipLogic.branches[${String(index)}] for "${questionKey}" must be an object.`);
        continue;
      }

      const rule = rawRule as TenantAdminOutboundQuestionBranchRuleV1;
      const condition = readBranchRuleCondition(rule);
      const targetQuestionKey = readBranchTarget(rule);
      const path = `skipLogic.branches[${String(index)}] for "${questionKey}"`;

      if (!condition) {
        errors.push(`${path} must include when, condition, or if.`);
      } else {
        validateCondition({
          condition,
          ownerQuestionKey: questionKey,
          path,
          requireQuestionKey: false,
        });
      }

      if (!targetQuestionKey && rule.end !== true) {
        errors.push(`${path} must include nextQuestionKey, goToQuestionKey, or end: true.`);
      }

      if (targetQuestionKey && !questionKeys.has(targetQuestionKey)) {
        errors.push(`${path} references missing question "${targetQuestionKey}".`);
      }

      if (targetQuestionKey === questionKey) {
        errors.push(`${path} cannot branch back to the same question.`);
      }
    }
  }

  return {
    errors,
    ok: errors.length === 0,
  };
}

export interface TenantAdminOutboundClinicalApprovalInputV1 {
  approvedByReference: string;
  approvedByDisplay: string;
  approvedAt?: string | null;
  approvalNotes?: string | null;
}

export interface TenantAdminOutboundCampaignPolicyInputV1 {
  policyKey?: string | null;
  timezoneMode?: TenantAdminOutboundTimezoneModeV1 | null;
  defaultTimezone?: string | null;
  allowedDays?: string[] | null;
  allowedStartLocalTime?: string | null;
  allowedEndLocalTime?: string | null;
  quietHours?: Record<string, unknown> | null;
  blackoutDates?: string[] | null;
  maxAttemptsPerDay?: number | null;
  maxAttemptsTotal?: number | null;
  minRetryCooldownMinutes?: number | null;
  callCapMinutes?: number | null;
  voicemailPolicy?: TenantAdminOutboundVoicemailPolicyV1 | null;
  recordingPolicy?: TenantAdminOutboundRecordingPolicyV1 | null;
  resumePolicy?: Record<string, unknown> | null;
  optOutPolicy?: Record<string, unknown> | null;
  policyMetadata?: Record<string, unknown> | null;
}

export interface TenantAdminOutboundCampaignDialerConfigInputV1 {
  dialerMode?: TenantAdminOutboundDialerModeV1 | null;
  dialerStatus?: TenantAdminOutboundCampaignDialerStatusV1 | null;
  managedTelephony?: boolean | null;
  connectInstanceId?: string | null;
  connectCampaignId?: string | null;
  connectCampaignArn?: string | null;
  connectCampaignRegion?: string | null;
  connectContactFlowId?: string | null;
  connectSourcePhoneNumber?: string | null;
  metadata?: Record<string, unknown> | null;
}

export interface TenantAdminOutboundCallsCommandRequestV1 {
  schemaVersion: typeof tenantAdminOutboundCallsCommandContractVersion;
  tenantId: string;
  command: TenantAdminOutboundCallCommandInputV1;
}

export interface TenantAdminOutboundCallsCommandResponseV1 {
  schemaVersion: typeof tenantAdminOutboundCallsCommandContractVersion;
  tenantId: string;
  commandId: string;
  appliedAt: string;
  summaryText: string;
  affectedCampaignId?: string | null;
  affectedQuestionnaireTemplateId?: string | null;
  affectedQuestionnaireVersionId?: string | null;
  affectedTargetId?: string | null;
  affectedSessionId?: string | null;
  campaignQuestionnaireImpactReview?: TenantAdminOutboundCampaignQuestionnaireImpactReviewV1 | null;
  summary: TenantAdminOutboundCallsSummaryResponseV1;
  changedScopes?: WorkspaceChangedScopeV1[];
}
